import { computed, onMounted, reactive, ref } from 'vue'

const todayRange = () => {
  const today = new Date()
  const y = today.getFullYear()
  const m = String(today.getMonth() + 1).padStart(2, '0')
  const d = String(today.getDate()).padStart(2, '0')
  const value = `${y}-${m}-${d}`
  return [value, value]
}
import { ElMessage } from 'element-plus'
import { request } from '../services/adminApi'

const pageRows = (data) => Array.isArray(data) ? data : data?.records || data?.rows || data?.list || []

export function useCustomer() {
  const token = ref(localStorage.getItem('customerToken') || '')
  const phone = ref(localStorage.getItem('customerPhone') || '')
  const loading = ref(false)
  const recordsLoading = ref(false)
  const info = reactive({ balance: 0, adEnabled: 1, riskEnabled: 1, status: 1 })
  const apps = ref([])
  const devices = ref([])
  const groups = ref([])
  const records = ref([])
  const recordPlatforms = ref([])
  const recordPagination = reactive({ page: 1, size: 10, total: 0, totalReward: 0 })

  const recordFilters = reactive({
    appId: null,
    groupId: null,
    deviceId: '',
    userId: '',
    adPlatform: null,
    adType: null,
    dateRange: todayRange()
  })

  const call = (path, options) => request(path, token.value, options)

  const uniqueAdPlatforms = computed(() => recordPlatforms.value)
  const uniqueAdTypes = computed(() => ['reward', 'interstitial', 'banner'])

  function resolveRecordDateRange() {
    const range = recordFilters.dateRange
    if (range?.[0] && range?.[1]) {
      return { startDate: range[0], endDate: range[1] }
    }
    const [startDate, endDate] = todayRange()
    return { startDate, endDate }
  }

  async function loadRecordPlatforms() {
    const { startDate, endDate } = resolveRecordDateRange()
    const params = new URLSearchParams({ startDate, endDate })
    if (recordFilters.appId != null) params.set('appId', String(recordFilters.appId))
    const data = await call(`/api/customer/ad/platforms?${params}`)
    recordPlatforms.value = Array.isArray(data) ? data : []
  }

  async function loadRecords(options = {}) {
    if (options.page != null) recordPagination.page = options.page
    if (options.size != null) recordPagination.size = options.size
    const { startDate, endDate } = resolveRecordDateRange()
    const params = new URLSearchParams({
      page: String(recordPagination.page),
      size: String(recordPagination.size),
      startDate,
      endDate
    })
    if (recordFilters.appId != null) params.set('appId', String(recordFilters.appId))
    if (recordFilters.groupId != null) params.set('groupId', recordFilters.groupId)
    if (recordFilters.deviceId) params.set('deviceId', recordFilters.deviceId)
    if (recordFilters.userId) params.set('userId', recordFilters.userId)
    if (recordFilters.adPlatform) params.set('adPlatform', recordFilters.adPlatform)
    if (recordFilters.adType) params.set('adType', recordFilters.adType)

    recordsLoading.value = true
    try {
      const data = await call(`/api/customer/ad/record/list?${params}`)
      records.value = pageRows(data)
      recordPagination.total = Number(data?.total || 0)
      recordPagination.totalReward = data?.totalReward ?? 0
      await loadRecordPlatforms()
    } finally {
      recordsLoading.value = false
    }
  }

  async function searchRecords() {
    await loadRecords({ page: 1 })
  }

  async function onRecordPageChange({ page, size }) {
    await loadRecords({ page, size })
  }

  async function loadAll(showMessage = false) {
    loading.value = true
    try {
      const [infoData, appData, deviceData, groupData] = await Promise.all([
        call('/api/customer/profile'),
        call('/api/customer/device/app/list'),
        call('/api/customer/device/list'),
        call('/api/customer/device/group/list')
      ])
      Object.assign(info, {
        balance: infoData?.balance || 0,
        adEnabled: infoData?.adEnabled ?? 1,
        riskEnabled: infoData?.riskEnabled ?? 1,
        status: infoData?.status ?? 1
      })
      apps.value = appData || []
      devices.value = deviceData || []
      groups.value = groupData || []
      // 仅一个应用时默认选中，与 App 端「当前包名」总金币对齐
      if (apps.value.length === 1 && recordFilters.appId == null) {
        recordFilters.appId = apps.value[0].id
      }
      await loadRecords({ page: 1 })
      if (showMessage) ElMessage.success('数据已刷新')
    } catch (error) {
      ElMessage.error(error.message)
    } finally {
      loading.value = false
    }
  }

  async function login(form) {
    loading.value = true
    try {
      const data = await request('/api/customer/auth/login', '', { method: 'POST', body: JSON.stringify(form) })
      token.value = data.token
      phone.value = data.phone
      localStorage.setItem('customerToken', data.token)
      localStorage.setItem('customerPhone', data.phone)
      await loadAll()
    } catch (error) {
      ElMessage.error(error.message)
    } finally {
      loading.value = false
    }
  }

  function logout() {
    localStorage.removeItem('customerToken')
    localStorage.removeItem('customerPhone')
    token.value = ''
  }

  async function clearRecordFilters() {
    recordFilters.appId = null
    recordFilters.groupId = null
    recordFilters.deviceId = ''
    recordFilters.userId = ''
    recordFilters.adPlatform = null
    recordFilters.adType = null
    recordFilters.dateRange = todayRange()
    try {
      await loadRecords({ page: 1 })
    } catch (error) {
      ElMessage.error(error.message)
    }
  }

  onMounted(() => token.value && loadAll())

  return {
    token, phone, loading, recordsLoading, info, apps, devices, groups, records,
    recordFilters, recordPagination, uniqueAdPlatforms, uniqueAdTypes,
    clearRecordFilters, searchRecords, onRecordPageChange, loadRecords,
    login, logout, loadAll
  }
}
